<template>
  <div class="relative inline-flex items-center">
    <!-- Tooltip trigger -->
    <div
      ref="triggerRef"
      class="focus:outline-hidden rounded"
      :class="triggerClass"
      :aria-describedby="isTooltipVisible ? 'tooltip-content' : undefined"
      @mouseenter="showTooltip"
      @mouseleave="startHideTooltip"
      @focus="showTooltip"
      @blur="startHideTooltip"
      @keydown.escape="hideTooltip"
    >
      <slot name="trigger">
        <button
          type="button"
          class="flex items-center focus:ring-2 focus:ring-offset-2 focus:ring-black"
        >
          <mdicon :aria-label="ariaLabel" :name="icon" :size="size"></mdicon>
        </button>
      </slot>
    </div>

    <!-- Tooltip -->
    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isTooltipVisible"
        id="tooltip-content"
        ref="tooltipRef"
        role="tooltip"
        class="fixed z-50 max-w-72 w-max text-sm text-white bg-black rounded-md shadow-lg"
        :style="tooltipPosition"
        @mouseenter="showTooltip"
        @mouseleave="startHideTooltip"
      >
        <div
          class="relative bg-black text-white text-sm rounded-lg shadow-md p-3"
        >
          <!-- Tooltip text content -->
          <span class="block"><slot /></span>
          <!-- Tooltip arrow -->
          <div
            class="absolute"
            :class="[
              isTooltipLeft
                ? '-right-1 top-1/2 -translate-y-1/2'
                : 'inset-x-0 -bottom-1',
            ]"
            aria-hidden="true"
          >
            <div class="bg-black h-2 w-2 relative flex mx-auto rotate-45"></div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'

const props = defineProps({
  icon: {
    type: String,
    default: 'help-circle-outline',
  },
  size: {
    type: Number,
    default: 20,
  },
  ariaLabel: {
    type: String,
    default: 'More information',
  },
  triggerClass: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const isTooltipVisible = ref(false)
const tooltipRef = ref(null)
const tooltipPosition = ref({})
const isTooltipLeft = ref(false)
let hideTimeout = null

const triggerRef = ref(null)

const calculateTooltipPosition = async () => {
  if (!isTooltipVisible.value) return

  await nextTick()
  const button = triggerRef.value
  if (!button) return

  const rect = button.getBoundingClientRect()
  const tooltipWidth = tooltipRef.value?.offsetWidth || 0
  const tooltipHeight = tooltipRef.value?.offsetHeight || 0
  const windowWidth = window.innerWidth
  const windowHeight = window.innerHeight

  // Check if tooltip would go off right edge
  const wouldOverflowRight = rect.left + tooltipWidth / 2 > windowWidth - 16

  // Position tooltip above or below based on space
  isTooltipLeft.value = wouldOverflowRight

  let top, left

  if (wouldOverflowRight) {
    // Position tooltip to the left with spacing
    left = rect.right - tooltipWidth - 24
    top = rect.top + rect.height / 2 - tooltipHeight / 2
  } else {
    // Default centered position
    left = rect.left - tooltipWidth / 2 + rect.width / 2
    top = rect.top - tooltipHeight - 8
  }

  // Prevent going off left edge
  left = Math.max(16, left)
  // Prevent going off right edge
  left = Math.min(left, windowWidth - tooltipWidth - 16)
  // Prevent going off top edge
  top = Math.max(8, top)
  // Prevent going off bottom edge
  top = Math.min(top, windowHeight - tooltipHeight - 8)

  tooltipPosition.value = {
    top: `${top}px`,
    left: `${left}px`,
  }
}

const showTooltip = async () => {
  if (props.disabled) return
  if (hideTimeout) {
    clearTimeout(hideTimeout)
  }
  isTooltipVisible.value = true
  await calculateTooltipPosition()
}

const hideTooltip = () => {
  isTooltipVisible.value = false
  if (hideTimeout) {
    clearTimeout(hideTimeout)
  }
}

const startHideTooltip = () => {
  hideTimeout = setTimeout(() => {
    isTooltipVisible.value = false
  }, 100)
}

const handleResize = () => {
  calculateTooltipPosition()
}

const handleScroll = () => {
  if (isTooltipVisible.value) {
    isTooltipVisible.value = false
  }
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  window.addEventListener('scroll', handleScroll, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('scroll', handleScroll, true)
  if (hideTimeout) {
    clearTimeout(hideTimeout)
  }
})
</script>
